// The customer at a glance on the call (the call-handling parity, item 1): one stat line (BL-099,
// Brook's tighter card) of how long they've been a customer, their jobs, what they've been billed,
// what they owe (always, $0.00 too, for those who may see invoices) and their membership; then, only
// when there is something, open estimates, the equipment at the address, their notes and the
// address's site contact, and a strip of their newest photos that opens the viewer (BL-131); and the
// way to their page.
import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { money } from '../books/model.ts';
import { customerHref } from '../history/customer-link.ts';
import { openJob } from '../job/job-page.ts';
import { campaignWord } from './source-model.ts';
import { phoneText } from '../types.ts';
import { PhotoViewer } from '../shared/PhotoViewer.tsx';
import '../review/review.css';
import {
  GLANCE_PHOTOS_ASKED,
  photoStrip,
  equipmentWord,
  jobsWord,
  lastVisitWord,
  membershipWord,
  moreWord,
  sinceWord,
  type Glance,
  type GlancePhoto,
} from './glance-model.ts';

/** While it loads, the line holds its place (the customer page's pebbles). */
function Waiting() {
  return (
    <div className="call-glance" aria-busy="true">
      <div className="call-glance-stats" aria-label="At a glance">
        <span className="ph-pebble" aria-hidden="true" />
      </div>
    </div>
  );
}

/**
 * Their newest photos (BL-131), read after the glance so the card never waits on them: customers.photos,
 * so only the jobs this person sees, and the thumbnails load as they come into view. Nothing shows
 * while it reads, for a customer with no photos, or if the read fails: the glance shows what's there.
 */
function GlancePhotos({ customerId, timeZone }: { customerId: string; timeZone: string }) {
  const [photos, setPhotos] = useState<{ customerId: string; list: GlancePhoto[] } | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    void client
      .call<{ photos: GlancePhoto[] }>('customers.photos', { customerId, limit: GLANCE_PHOTOS_ASKED })
      .then((r) => live && setPhotos({ customerId, list: r.photos }))
      .catch(() => live && setPhotos(null));
    return () => {
      live = false;
    };
  }, [customerId]);
  const strip = photos?.customerId === customerId ? photoStrip(photos.list, timeZone) : [];
  if (!strip.length) return null;
  return (
    <>
      <dt>Photos</dt>
      <dd>
        <ul className="photo-grid" aria-label="Their newest photos">
          {strip.map((s, i) => (
            <li key={s.photo.id} className="pg-tile">
              <button
                type="button"
                className="pg-open"
                aria-label={`Open ${s.name}, job #${s.photo.jobNumber}, full size`}
                onClick={() => setViewing(i)}
              >
                <img src={s.src} alt="" loading="lazy" decoding="async" />
              </button>
              <span className="small muted pg-caption">
                {s.name}
                {s.when && `, ${s.when}`}
              </span>
            </li>
          ))}
        </ul>
        <a className="btn quiet sm" href={customerHref(customerId, 'photos')}>
          All their photos
        </a>
        {viewing !== null && (
          <PhotoViewer
            photos={strip.map((s) => s.photo)}
            start={viewing}
            timeZone={timeZone}
            onClose={() => setViewing(null)}
          />
        )}
      </dd>
    </>
  );
}

/** `owingCents` is customers.money's open total, once it's read, for those the glance shows billed to. */
export function CallGlance({
  g,
  owingCents,
  timeZone,
}: {
  g: Glance | null;
  owingCents?: number;
  /** The company's: the day each photo was taken. */
  timeZone: string;
}) {
  if (!g) return <Waiting />;
  const last = lastVisitWord(g);
  return (
    <div className="call-glance">
      <div className="call-glance-stats" aria-label="At a glance">
        <span className="call-glance-stat">
          Customer since <b>{sinceWord(g)}</b>
        </span>
        <span className="call-glance-stat">
          <b>{jobsWord(g)}</b>
        </span>
        {last && <span className="call-glance-stat">{last}</span>}
        {g.billedCents !== null && (
          <span className="call-glance-stat">
            Billed <b>{money(g.billedCents)}</b>
          </span>
        )}
        {g.billedCents !== null && owingCents !== undefined && (
          <span className={`call-glance-stat${owingCents > 0 ? ' owes' : ''}`}>
            Owing <b>{money(owingCents)}</b>
          </span>
        )}
        {g.membership && <span className="call-glance-stat">Member: {membershipWord(g.membership)}</span>}
      </div>
      <dl className="kv call-glance-facts">
        {g.firstCampaign && (
          <>
            <dt>First came from</dt>
            <dd>{campaignWord(g.firstCampaign)}</dd>
          </>
        )}
        {g.openEstimates.count > 0 && (
          <>
            <dt>Open estimates</dt>
            <dd>
              <ul className="call-glance-list">
                {g.openEstimates.listed.map((e) => (
                  <li key={e.id}>
                    <button type="button" className="btn quiet sm" onClick={() => openJob(e.jobId)}>
                      #{e.number} {e.title}
                    </button>
                  </li>
                ))}
              </ul>
              {moreWord(g.openEstimates.count, g.openEstimates.listed.length) && (
                <span className="muted small">
                  {moreWord(g.openEstimates.count, g.openEstimates.listed.length)}
                </span>
              )}
            </dd>
          </>
        )}
        {g.equipment.count > 0 && (
          <>
            <dt>Equipment here</dt>
            <dd>
              <ul className="call-glance-list">
                {g.equipment.listed.map((e) => (
                  <li key={e.id}>{equipmentWord(e)}</li>
                ))}
              </ul>
              {moreWord(g.equipment.count, g.equipment.listed.length) && (
                <span className="muted small">{moreWord(g.equipment.count, g.equipment.listed.length)}</span>
              )}
            </dd>
          </>
        )}
        {(g.about || g.notes.length > 0) && (
          <>
            <dt>Notes</dt>
            <dd>
              {g.about && <p className="call-glance-note">{g.about}</p>}
              {g.notes.map((n, i) => (
                <p key={i} className="call-glance-note">
                  {n.body}
                </p>
              ))}
            </dd>
          </>
        )}
        <GlancePhotos customerId={g.customerId} timeZone={timeZone} />
        {g.siteContact && (
          <>
            <dt>Site contact</dt>
            <dd>
              {g.siteContact.name}
              {g.siteContact.phone && ` · ${phoneText(g.siteContact.phone)}`}
            </dd>
          </>
        )}
      </dl>
      <a className="btn" href={customerHref(g.customerId)}>
        Open customer
      </a>
    </div>
  );
}
